import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { AssetAmount } from "@sundaeswap/asset";
import { useHandleDepositRequest } from "@/hooks";
import { useHydrozoaHeadInfo } from "@/hooks/useHydrozoaScriptAddress";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { WalletConnectDialog } from "@/components/WalletConnect/WalletConnectDialog";
import { IntroStep } from "./IntroStep";
import { ConnectStep } from "./ConnectStep";
import { AmountStep, DepositEntry } from "./AmountStep";
import { ReviewStep } from "./ReviewStep";
import { ProcessingStep } from "./ProcessingStep";
import { SuccessStep } from "./SuccessStep";

export type DepositStep =
  | "intro"
  | "connect"
  | "amount"
  | "review"
  | "processing"
  | "success";

interface DepositOverlayProps {
  open: boolean;
  onClose: () => void;
  viewRequestId?: string | null;
}

const STEP_ORDER: DepositStep[] = ["intro", "connect", "amount", "review", "processing", "success"];

export const DepositOverlay: FC<DepositOverlayProps> = ({ open, onClose, viewRequestId }) => {
  const [step, setStep] = useState<DepositStep>("intro");
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [animKey, setAnimKey] = useState(0);
  const [closing, setClosing] = useState(false);
  const [depositEntries, setDepositEntries] = useState<DepositEntry[]>([
    { assetId: "ada.lovelace", amount: "" },
  ]);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);

  const navigateTo = useCallback((next: DepositStep) => {
    const curIdx = STEP_ORDER.indexOf(step);
    const nextIdx = STEP_ORDER.indexOf(next);
    setDirection(nextIdx >= curIdx ? "forward" : "back");
    setAnimKey((k) => k + 1);
    setStep(next);
  }, [step]);
  const { activeWallet } = useWalletObserver();
  const { mutateAsync } = useHandleDepositRequest();
  const { depositTxs, deposits, accounts } = useGummiwormContext();
  const hasL2Funds = accounts.length > 0;
  const headInfo = useHydrozoaHeadInfo();
  const headOnline = !!headInfo;

  const isConnected = !!activeWallet;

  // When opened, decide which step to start on
  useEffect(() => {
    if (!open) return;
    if (viewRequestId) {
      setActiveRequestId(viewRequestId);
      setStep("processing");
    } else if (hasL2Funds && isConnected) {
      // Already deposited before — skip the intro
      setStep("amount");
    }
  }, [open, viewRequestId, hasL2Funds, isConnected]);

  // When wallet connects while on the connect step, advance to amount
  useEffect(() => {
    if (step === "connect" && isConnected) {
      setStep("amount");
    }
  }, [step, isConnected]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setStep("intro");
      setDepositEntries([{ assetId: "ada.lovelace", amount: "" }]);
      setActiveRequestId(null);
      setError(null);
      onClose();
    }, 200);
  }, [onClose]);

  const handleBeginDeposit = useCallback(() => {
    if (isConnected) {
      navigateTo("amount");
    } else {
      navigateTo("connect");
    }
  }, [isConnected, navigateTo]);

  const handleAmountConfirm = useCallback(() => {
    navigateTo("review");
  }, [navigateTo]);

  const handleDeposit = useCallback(async () => {
    setError(null);
    navigateTo("processing");
    try {
      const amounts = depositEntries
        .filter((e) => Number(e.amount) > 0)
        .map((e) =>
          AssetAmount.fromValue(Number(e.amount), {
            decimals: 6,
            assetId: e.assetId,
          }),
        );
      const result = await mutateAsync({ depositAmounts: amounts });
      const id = (result as { requestId: number }).requestId.toString();
      setActiveRequestId(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      navigateTo("review");
    }
  }, [depositEntries, mutateAsync, navigateTo]);

  const handleSwitchWallet = useCallback(() => {
    setWalletDialogOpen(true);
  }, []);

  const activeTx = activeRequestId
    ? depositTxs.get(activeRequestId)
    : null;
  const activeStatus = activeTx?.status ?? null;

  const primaryDepositAmount = depositEntries[0]?.amount ?? "";

  const effectiveAmount = useMemo(() => {
    if (!activeRequestId) return primaryDepositAmount;
    const dep = deposits.find(
      (d) => d.requestId.toString() === activeRequestId,
    );
    if (!dep) return primaryDepositAmount;
    const ada = dep.value.assets.find((a) => a.asset.tag === "Ada");
    if (!ada) return primaryDepositAmount;
    return new AssetAmount(ada.value, {
      decimals: 6,
      assetId: "ada.lovelace",
    }).value.toString();
  }, [activeRequestId, deposits, primaryDepositAmount]);

  // ProcessingStep now handles the complete state directly (shows "Funds Deposited!")

  // Build the ordered list of steps for this session
  const steps = useMemo<DepositStep[]>(() => {
    const s: DepositStep[] = [];
    if (!hasL2Funds || !isConnected) s.push("intro");
    if (!isConnected) s.push("connect");
    s.push("amount", "review", "processing", "success");
    return s;
  }, [isConnected, hasL2Funds]);

  const currentIndex = steps.indexOf(step);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div
      className={`sr-overlay ${closing ? "sr-overlay-exit" : "sr-overlay-enter"}`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        key={animKey}
        className={`w-full max-w-[420px] flex flex-col gap-8 items-center ${
          direction === "forward" ? "sr-step-forward" : "sr-step-back"
        }`}
      >
        {step === "intro" && <IntroStep onBegin={handleBeginDeposit} />}
        {step === "connect" && (
          <ConnectStep onBack={() => navigateTo("intro")} />
        )}
        {step === "amount" && (
          <AmountStep
            entries={depositEntries}
            onEntriesChange={setDepositEntries}
            onConfirm={handleAmountConfirm}
            onBack={hasL2Funds && isConnected ? handleClose : () => navigateTo(isConnected ? "intro" : "connect")}
            onSwitchWallet={isConnected ? handleSwitchWallet : undefined}
          />
        )}
        {step === "review" && (
          <ReviewStep
            entries={depositEntries}
            error={error}
            headOnline={headOnline}
            onConfirm={handleDeposit}
            onBack={() => navigateTo("amount")}
            onSwitchWallet={isConnected ? handleSwitchWallet : undefined}
          />
        )}
        {step === "processing" && (
          <ProcessingStep
            requestId={activeRequestId}
            status={activeStatus ?? null}
            error={error}
            estimatedAbsorptionTime={activeTx?.estimatedAbsorptionTime}
            amount={effectiveAmount}
            txHash={activeTx?.hash}
            onClose={handleClose}
          />
        )}
        {step === "success" && (
          <SuccessStep amount={effectiveAmount} onClose={handleClose} />
        )}

        {/* Step indicator dots (hidden during processing/success) */}
        {step !== "processing" && step !== "success" && (
          <div className="flex gap-1 items-center justify-center">
            {steps.map((_, i) => (
              <div
                key={i}
                className={i === currentIndex ? "sr-dot-active" : "sr-dot"}
              />
            ))}
          </div>
        )}
      </div>

      <WalletConnectDialog
        open={walletDialogOpen}
        onClose={() => setWalletDialogOpen(false)}
      />
    </div>
  );
};
